import { useEffect, useState } from 'react';
import { Image, ImageSourcePropType, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { ActionHeader } from '../components/AppHeader';
import BottomTabBar from '../components/BottomTabBar';
import colors from '../theme';
import { RootStackParamList } from '../navigation/RootNavigator';
import { Audiobook, getAudiobookBySlug } from '../api/audiobooks';
import { useFavoriteState } from '../hooks/useFavoriteState';
import { narratorName } from '../utils/audiobookPeople';

type Props = NativeStackScreenProps<RootStackParamList, 'BookInformation'>;

const coverImage = require('../../assets/aural-continue-thumb.png');

function imageSource(url: string | null | undefined): ImageSourcePropType {
  return url ? { uri: url } : coverImage;
}

function authorName(book: Audiobook | null) {
  return narratorName(book) || 'Đang cập nhật';
}

function formatDuration(seconds?: number | null) {
  if (!seconds) return 'Đang cập nhật';
  const minutes = Math.round(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return hours > 0 ? `${hours} giờ ${rest} phút` : `${minutes} phút`;
}

function DetailSkeleton() {
  return (
    <>
      <View style={styles.hero}>
        <View style={[styles.cover, styles.skeletonBlock]} />
        <View style={styles.heroDetails}>
          <View style={[styles.skeletonBlock, styles.skeletonTitle]} />
          <View style={[styles.skeletonBlock, styles.skeletonLineSmall]} />
          <View style={[styles.skeletonBlock, styles.skeletonLineMedium]} />
        </View>
      </View>
      <View style={styles.infoList}>
        {[0, 1, 2, 3, 4].map((item) => (
          <View key={item} style={styles.infoRow}>
            <View style={[styles.skeletonBlock, styles.skeletonInfoLabel]} />
            <View style={[styles.skeletonBlock, styles.skeletonInfoValue]} />
          </View>
        ))}
      </View>
    </>
  );
}

export default function BookInformationScreen({ navigation, route }: Props) {
  const [book, setBook] = useState<Audiobook | null>(null);
  const params = route.params;

  useEffect(() => {
    let mounted = true;
    setBook(null);
    if (params?.slug) {
      getAudiobookBySlug(params.slug).then((data) => mounted && setBook(data)).catch(() => mounted && setBook(null));
    }
    return () => {
      mounted = false;
    };
  }, [params?.slug]);

  const detailParams = { id: book?.id ?? params?.id, slug: book?.slug ?? params?.slug };
  const favoriteState = useFavoriteState(book?.id ?? params?.id, () => navigation.navigate('Login'));
  const rows = [
    ['Người đọc sách', authorName(book)],
    ['Thời lượng', formatDuration(book?.duration)],
    ['Ngày phát hành', book?.released_at ?? 'Đang cập nhật'],
    ['Ngôn ngữ', book?.language ?? 'vi'],
    ['Nhà xuất bản', book?.publisher ?? 'Đang cập nhật'],
    ['Lượt nghe', String(book?.views ?? 0)],
  ];

  return (
    <View style={styles.screen}>
      <ScrollView contentContainerStyle={styles.container} showsVerticalScrollIndicator={false}>
        <ActionHeader
          onBack={() => navigation.goBack()}
          rightIcons={[favoriteState.favorite ? 'heart' : 'heart-outline', 'ellipsis-horizontal']}
          onRightPress={[favoriteState.toggle]}
        />

        {!book ? (
          <DetailSkeleton />
        ) : (
          <>
            <View style={styles.hero}>
              <Image source={imageSource(book.cover)} style={styles.cover} resizeMode="cover" />
              <View style={styles.heroDetails}>
                <Text style={styles.title}>{book.name}</Text>
                <Text style={styles.author}>{authorName(book)}</Text>
                <View style={styles.ratingRow}>
                  <Text style={styles.stars}>{'\u2605\u2605\u2605\u2605\u2605'}</Text>
                  <Text style={styles.rating}>{book.rating_average ?? 0} / 5.0 ({book.rating_count ?? 0})</Text>
                </View>
              </View>
            </View>

            <View style={styles.buttons}>
              <Pressable style={styles.primaryButton} onPress={() => navigation.navigate('Player', detailParams)}>
                <Ionicons name="play" size={20} color={colors.buttonText} />
                <Text style={styles.primaryText}>Nghe ngay</Text>
              </Pressable>
            </View>

            <View style={styles.tabs}>
              <Pressable style={styles.tab} onPress={() => navigation.navigate('BookOverview', detailParams)}><Text style={styles.tabText}>Tổng quan</Text></Pressable>
              <Pressable style={[styles.tab, styles.tabActive]}><Text style={[styles.tabText, styles.tabTextActive]}>Thông tin</Text></Pressable>
              <Pressable style={styles.tab} onPress={() => navigation.navigate('BookReviews', detailParams)}><Text style={styles.tabText}>Đánh giá</Text></Pressable>
            </View>

            <View style={styles.infoList}>
              {rows.map(([label, value]) => (
                <View key={label} style={styles.infoRow}>
                  <Text style={styles.infoLabel}>{label}</Text>
                  <Text style={styles.infoValue}>{value}</Text>
                </View>
              ))}
            </View>
          </>
        )}
      </ScrollView>
      <BottomTabBar
        active="Home"
        onHome={() => navigation.navigate('Home')}
        onDiscover={() => navigation.navigate('GetSuggestions')}
        onLibrary={() => navigation.navigate('Library')}
        onProfile={() => navigation.navigate('Settings')}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: 'rgb(242, 249, 253)' },
  container: { paddingHorizontal: 24, paddingTop: 62, paddingBottom: 124 },
  hero: { flexDirection: 'row', alignItems: 'center', gap: 24, marginBottom: 32 },
  cover: { width: 100, height: 100, borderRadius: 8, backgroundColor: colors.card },
  heroDetails: { width: 197, gap: 8 },
  title: { color: colors.textPrimary, fontSize: 20, fontWeight: '600' },
  author: { color: colors.textSecondary, fontSize: 14 },
  ratingRow: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  stars: { color: '#FFB200', fontSize: 14, letterSpacing: 0 },
  rating: { color: colors.textPrimary, fontSize: 14 },
  buttons: { flexDirection: 'row', gap: 16, marginBottom: 24 },
  primaryButton: { flex: 1, height: 48, borderRadius: 8, backgroundColor: colors.button, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8 },
  secondaryButton: { flex: 1, height: 48, borderRadius: 8, borderWidth: 1, borderColor: colors.textPrimary, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8 },
  primaryText: { color: colors.buttonText, fontSize: 14, fontWeight: '600' },
  secondaryText: { color: colors.textPrimary, fontSize: 14, fontWeight: '600' },
  tabs: { flexDirection: 'row', marginBottom: 24 },
  tab: { width: 109, paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: colors.border, alignItems: 'center' },
  tabActive: { borderBottomColor: colors.textPrimary },
  tabText: { color: colors.textSecondary, fontSize: 14 },
  tabTextActive: { color: colors.textPrimary, fontWeight: '500' },
  infoList: { gap: 8 },
  infoRow: { minHeight: 48, flexDirection: 'row', alignItems: 'center', gap: 24 },
  infoLabel: { width: 110, color: colors.textSecondary, fontSize: 14 },
  infoValue: { flex: 1, color: colors.textPrimary, fontSize: 14, fontWeight: '500' },
  skeletonBlock: { backgroundColor: '#ECEEF2' },
  skeletonTitle: { width: 176, height: 22, borderRadius: 6 },
  skeletonLineSmall: { width: 96, height: 14, borderRadius: 6 },
  skeletonLineMedium: { width: 132, height: 14, borderRadius: 6 },
  skeletonInfoLabel: { width: 110, height: 14, borderRadius: 6 },
  skeletonInfoValue: { flex: 1, height: 14, borderRadius: 6 },
});
